import {useCallback, useEffect, useRef, useState} from 'react';
import {mergeMessages, reconcileMessagePage} from '../chatUtils';
import {isServerMessage} from '../serverMessages';

// A history window stays contiguous. Live catch-up must not append the latest
// page to an older window, since doing so would silently skip intervening pages.
export default function useMessageNavigation({owner, fetchPage, messagesRef, setMessages, setHasMore, setError, timelineRef}) {
    const activeRef = useRef(false);
    const committedActive = useRef(false);
    const epoch = useRef(0);
    const request = useRef(null);
    const activeOwner = useRef(owner);
    activeOwner.current = owner;
    const [state, setState] = useState({active: false, loading: false, hasNewer: false, target: null});

    useEffect(() => {
        const navigationEpoch = epoch;
        activeRef.current = false;
        committedActive.current = false;
        navigationEpoch.current++;
        setState({active: false, loading: false, hasNewer: false, target: null});
        return () => { request.current?.abort(); navigationEpoch.current++; };
    }, [owner]);

    const load = useCallback(async (mode, messageId) => {
        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        const generation = ++epoch.current;
        activeRef.current = true;
        setState(current => ({...current, loading: true}));
        const valid = () => !controller.signal.aborted && owner.mounted && activeOwner.current === owner && generation === epoch.current;
        const serverMessages = messagesRef.current.filter(isServerMessage);
        const options = mode === 'around' ? {aroundMessageId: messageId}
            : mode === 'older' ? {beforeSequence: serverMessages[0]?.sequence}
                : mode === 'newer' ? {afterSequence: serverMessages.at(-1)?.sequence} : {};
        const element = timelineRef.current;
        const oldHeight = element?.scrollHeight || 0;
        try {
            const payload = await fetchPage({...options, signal: controller.signal});
            if (!valid()) return false;
            const page = payload.messages || [];
            if (mode === 'around' && !page.some(message => message.id === messageId && !message.deleted_at)) {
                throw new Error('This message is no longer available.');
            }
            setMessages(current => mode === 'older' || mode === 'newer' ? mergeMessages(current, page) : reconcileMessagePage(current, page));
            if (mode !== 'newer') setHasMore(Boolean(payload.has_more_before));
            activeRef.current = mode !== 'latest';
            committedActive.current = activeRef.current;
            setState(current => ({
                active: mode !== 'latest', loading: false,
                hasNewer: mode === 'older' ? current.hasNewer : Boolean(payload.has_more_after),
                target: mode === 'around' ? {id: messageId, token: generation} : null,
            }));
            setError(null);
            if (mode === 'older' || mode === 'latest') window.requestAnimationFrame(() => {
                if (!valid() || !element) return;
                if (mode === 'older') element.scrollTop += element.scrollHeight - oldHeight;
                else element.scrollTop = element.scrollHeight;
            });
            return true;
        } catch (error) {
            if (valid()) {
                activeRef.current = committedActive.current;
                setState(current => ({...current, loading: false}));
                setError(error);
            }
            return false;
        }
    }, [fetchPage, messagesRef, owner, setError, setHasMore, setMessages, timelineRef]);

    const cancel = useCallback(() => {
        request.current?.abort();
        epoch.current++;
        activeRef.current = committedActive.current;
        setState(current => ({...current, loading: false}));
    }, []);
    const openAt = useCallback(id => load('around', id), [load]);
    const loadOlder = useCallback(() => load('older'), [load]);
    const loadNewer = useCallback(() => load('newer'), [load]);
    const returnToLatest = useCallback(() => load('latest'), [load]);
    return {...state, activeRef, epoch, cancel, openAt, loadOlder, loadNewer, returnToLatest};
}
